// Estado da SACOLA (bag) — lógica pura e testável, sem React.
//
// Cada linha da sacola é uma CONFIGURAÇÃO distinta do produto: mesmo produto com
// variação/adicionais diferentes coexiste como itens separados. A `key` garante
// isso: produtoId + variacaoId + adicionalIds ORDENADOS (ordem de escolha não
// importa). O `unitPrice` é calculado no cliente só para EXIBIÇÃO — o pedido
// envia apenas ids e o backend é a autoridade de preço.

import type { StoreProduto, StoreVariacao, StoreAdicionalOpcao, CategoriaCardapio, MontagemPizzaPayload } from './api';
import { computePizzaPreco } from './pizzaPreco';

export interface BagItem {
  key: string;
  produto: StoreProduto;
  variacao: StoreVariacao | null;
  adicionais: StoreAdicionalOpcao[];
  unitPrice: number;
  qty: number;
  /** Observação livre do cliente para ESTE item (ex.: "sem cebola"). Opcional; o backend já suporta
   *  (PedidoCardapioItem.observacao) — aditivo, nunca obrigatório. Vazia/whitespace vira `undefined`. */
  observacao?: string;
  montagemPizza?: MontagemPizzaPayload;
  pizzaResumo?: string[];
}

export const MAX_QTY = 99;

export function clampQty(q: number): number {
  const n = Math.round(Number.isFinite(q) ? q : 1);
  return Math.max(1, Math.min(MAX_QTY, n));
}

/** Chave estável: distingue configurações; independe da ordem dos adicionais. */
export function bagItemKey(
  produtoId: string,
  variacaoId: string | null | undefined,
  adicionalIds: string[],
  montagemPizza?: MontagemPizzaPayload,
): string {
  const ids = [...adicionalIds].sort();
  // Adicionais DENTRO da pizza (V388) só entram na chave quando existem: a pizza sem adicionais mantém a chave de
  // antes, e a sacola já salva no aparelho continua juntando a mesma pizza.
  const adicionaisPizza = montagemPizza?.adicionalIds?.length ? [[...montagemPizza.adicionalIds].sort()] : [];
  const pizza = montagemPizza ? JSON.stringify([
    montagemPizza.tamanhoId,
    [...montagemPizza.sabores].sort((a, b) => a.saborId.localeCompare(b.saborId)),
    montagemPizza.bordaId ?? '',
    ...adicionaisPizza,
  ]) : '';
  return `${produtoId}|${variacaoId ?? ''}|${ids.join(',')}|${pizza}`;
}

/**
 * F2-B7 — MESMA aritmética de `computeUnitPrice`, mas sem mascarar "montagem incompleta/indisponível" como
 * um preço de R$ 0,00: devolve `null` quando a pizza não é precificável ainda (0 sabores escolhidos, sabor
 * sem preço no tamanho, etc.). Uso: EXIBIÇÃO (ex.: o texto do botão "Adicionar" no QuickView) — `canAdd` já
 * bloqueia o ENVIO nesses casos, então isto é só sobre o cliente não ver "R$ 0,00" como se fosse de graça.
 */
export function computeUnitPriceOrNull(
  produto: StoreProduto,
  variacao: StoreVariacao | null,
  adicionais: StoreAdicionalOpcao[],
  montagemPizza?: MontagemPizzaPayload,
): number | null {
  if (produto.pizza && montagemPizza) {
    // C2 — a MESMA aritmética do servidor (PrecoPizzaService: BigDecimal HALF_EVEN), não Math.round
    // (half-up/ponto flutuante, que divergia em ~2,9% dos casos). Ver lib/pizzaPreco.ts.
    return computePizzaPreco(
      produto.pizza,
      montagemPizza.tamanhoId,
      montagemPizza.sabores,
      montagemPizza.bordaId,
      montagemPizza.adicionalIds,
    );
  }
  const base = variacao ? variacao.preco : produto.preco;
  const add = contribuicaoDosGrupos(adicionais);
  return Math.round((base + add) * 100) / 100;
}

/** Preço unitário para EXIBIÇÃO/PERSISTÊNCIA na sacola: variação (ou base) + Σ adicionais. `0` é o fallback
 *  DEFENSIVO de último recurso quando a pizza não é precificável (nunca deveria chegar aqui — a UI já impede
 *  essa seleção via A2/canAdd); use `computeUnitPriceOrNull` quando precisar distinguir "indisponível" de
 *  "R$ 0 de verdade" (F2-B7). */
/**
 * Quanto as opções escolhidas somam, RESPEITANDO A POLÍTICA DE CADA GRUPO (V315).
 *
 * `SOMA` (padrão histórico) soma tudo; `MAIOR` cobra só a mais cara daquele grupo — o "meia a meia", em
 * que o cliente leva metade de uma porção e metade de outra e paga a mais cara, não as duas. Grupos de
 * políticas diferentes convivem no mesmo item, por isso a conta é por grupo.
 *
 * GÊMEA de `PrecoItemService` (servidor) e de `frontend/src/lib/cart.ts` (PDV). Divergir significa o
 * cliente ler um preço no cardápio e o pedido chegar com outro.
 */
export function contribuicaoDosGrupos(adicionais: readonly StoreAdicionalOpcao[]): number {
  let soma = 0;
  const maiorPorGrupo = new Map<string, number>();
  for (const a of adicionais) {
    const preco = Number.isFinite(a.preco) ? a.preco : 0;
    // Sem grupo ou sem política = SOMA. O fallback é sempre o comportamento de sempre.
    if (a.politicaGrupo === 'MAIOR' && a.grupoId) {
      const atual = maiorPorGrupo.get(a.grupoId);
      if (atual === undefined || preco > atual) maiorPorGrupo.set(a.grupoId, preco);
    } else {
      soma += preco;
    }
  }
  for (const maior of maiorPorGrupo.values()) soma += maior;
  return soma;
}

export function computeUnitPrice(
  produto: StoreProduto,
  variacao: StoreVariacao | null,
  adicionais: StoreAdicionalOpcao[],
  montagemPizza?: MontagemPizzaPayload,
): number {
  return computeUnitPriceOrNull(produto, variacao, adicionais, montagemPizza) ?? 0;
}

/**
 * QA lote4 #6 — reprecifica a sacola contra o catálogo ATUAL (chamado ao ENTRAR no checkout). O produto/
 * variação/adicional pode ter mudado de preço depois de adicionado (a sacola persiste ~12h em localStorage);
 * sem isto o cliente via o preço ANTIGO nas linhas mas era cobrado o NOVO (o backend reprecifica no envio),
 * sem chance de recusar. Casa por id; item cujo produto sumiu do catálogo é preservado (o "esgotado" trata a
 * remoção). Devolve a nova sacola e se ALGUM preço mudou (para avisar o cliente).
 */
/**
 * A montagem de pizza do item ficou IMPOSSÍVEL de precificar no catálogo atual — um sabor, a borda ou um adicional saiu
 * do cardápio, ou o produto deixou de ser pizza. O servidor recusaria a montagem inteira, então o checkout trata a linha
 * como indisponível (avisa e oferece remover) em vez de mostrá-la por R$ 0,00.
 */
export function montagemIndisponivel(item: BagItem, fresh: StoreProduto): boolean {
  if (!item.montagemPizza) return false;
  if (!fresh.pizza) return true;
  return computeUnitPriceOrNull(fresh, null, [], item.montagemPizza) == null;
}

export function repriceBag(bag: BagItem[], categorias: CategoriaCardapio[]): { bag: BagItem[]; mudou: boolean } {
  const porId = new Map<string, StoreProduto>();
  for (const c of categorias) {
    for (const p of c.produtos) porId.set(p.produtoId, p);
  }
  let mudou = false;
  const nova = bag.map((item) => {
    const fresh = porId.get(item.produto.produtoId);
    if (!fresh) return item; // produto fora do catálogo atual: mantém (esgotado remove se for o caso)
    // Montagem impossível no catálogo atual: mantém o preço que tinha (nunca R$ 0,00); o checkout a marca indisponível.
    if (montagemIndisponivel(item, fresh)) return item;
    const freshVar = item.variacao
      ? (fresh.variacoes ?? []).find((v) => v.id === item.variacao!.id) ?? item.variacao
      : null;
    const freshAdd = item.adicionais.map((a) => {
      for (const g of fresh.adicionalGrupos ?? []) {
        const o = (g.opcoes ?? []).find((x) => x.id === a.id);
        // O preço é o do catálogo atual, mas o GRUPO e a política dele ficam na opção (QA 31/10/2026: sem eles a sacola
        // reprecificada voltava a somar um grupo "MAIOR").
        if (o) return { ...o, grupoId: g.id, politicaGrupo: g.politicaPreco ?? 'SOMA' };
      }
      return a;
    });
    const preco = computeUnitPrice(fresh, freshVar, freshAdd, item.montagemPizza);
    if (preco !== item.unitPrice) {
      mudou = true;
      return { ...item, produto: fresh, variacao: freshVar, adicionais: freshAdd, unitPrice: preco };
    }
    return item;
  });
  return { bag: nova, mudou };
}

export interface AddToBagInput {
  produto: StoreProduto;
  variacao: StoreVariacao | null;
  adicionais: StoreAdicionalOpcao[];
  qty: number;
  /** Observação livre para este item (QuickView). Trim aplicado; vazia vira `undefined`. */
  observacao?: string;
  montagemPizza?: MontagemPizzaPayload;
  pizzaResumo?: string[];
}

/** Adiciona (ou funde com uma linha idêntica) e devolve a nova sacola. */
export function addToBag(bag: BagItem[], input: AddToBagInput): BagItem[] {
  const { produto, variacao, adicionais, qty } = input;
  const key = bagItemKey(produto.produtoId, variacao?.id ?? null, adicionais.map((a) => a.id), input.montagemPizza);
  const unitPrice = computeUnitPrice(produto, variacao, adicionais, input.montagemPizza);
  // Trim + omite se vazia/whitespace — nunca guarda string vazia na sacola/no payload.
  const trimmed = input.observacao?.trim();
  const observacao = trimmed ? trimmed : undefined;
  const found = bag.find((i) => i.key === key);
  if (found) {
    // Mesma configuração: soma qty. Uma observação NOVA (não-vazia) substitui a anterior — é o que o
    // cliente acabou de digitar; sem observação nova na re-adição, preserva a que já estava na linha.
    return bag.map((i) =>
      i.key === key ? { ...i, qty: clampQty(i.qty + qty), observacao: observacao ?? i.observacao } : i
    );
  }
  return [...bag, {
    key, produto, variacao, adicionais, unitPrice, qty: clampQty(qty), observacao,
    ...(input.montagemPizza ? { montagemPizza: input.montagemPizza } : {}),
    ...(input.pizzaResumo ? { pizzaResumo: input.pizzaResumo } : {}),
  }];
}

/** Altera a quantidade; remove a linha quando chega a zero. */
export function changeQty(bag: BagItem[], key: string, delta: number): BagItem[] {
  return bag.flatMap((i) => {
    if (i.key !== key) return [i];
    const q = i.qty + delta;
    if (q <= 0) return [];
    return [{ ...i, qty: clampQty(q) }];
  });
}

export function removeItem(bag: BagItem[], key: string): BagItem[] {
  return bag.filter((i) => i.key !== key);
}

export function bagCount(bag: BagItem[]): number {
  return bag.reduce((a, b) => a + b.qty, 0);
}

export function bagSubtotal(bag: BagItem[]): number {
  return Math.round(bag.reduce((a, b) => a + b.unitPrice * b.qty, 0) * 100) / 100;
}
